import { useState } from 'react';
import TopNav from '../components/TopNav';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Code, Copy, CheckCircle, ExternalLink } from 'lucide-react';

export default function Embed() {
  const [domain, setDomain] = useState('techcrunch.com');
  const [style, setStyle] = useState<'button' | 'badge' | 'widget'>('button');
  const [copied, setCopied] = useState(false);

  const baseUrl = 'https://www.linkdiry.com';

  const snippets: Record<string, string> = {
    button: `<a href="${baseUrl}/domain/${domain}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:8px;padding:10px 20px;background:#0D9488;color:#fff;border-radius:10px;font-family:Inter,sans-serif;font-size:14px;font-weight:600;text-decoration:none;">Check Price on LinkDiry <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17L17 7M17 7H7M17 7V17"/></svg></a>`,
    badge: `<a href="${baseUrl}/domain/${domain}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:6px;padding:6px 14px;border:1px solid #E2E8F0;border-radius:8px;font-family:Inter,sans-serif;font-size:12px;color:#0F172A;text-decoration:none;background:#fff;"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0D9488" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg> Compare prices on <strong style="color:#0D9488;">LinkDiry</strong></a>`,
    widget: `<div style="border:1px solid #E2E8F0;border-radius:12px;padding:16px;max-width:320px;font-family:Inter,sans-serif;background:#fff;">
  <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;">
    <div style="width:32px;height:32px;background:#0D9488;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#fff;font-size:14px;font-weight:700;">${domain.charAt(0).toUpperCase()}</div>
    <div><div style="font-weight:600;font-size:14px;color:#0F172A;">${domain}</div><div style="font-size:11px;color:#94A3B8;">Guest post prices from 50+ sellers</div></div>
  </div>
  <a href="${baseUrl}/domain/${domain}" target="_blank" rel="noopener" style="display:block;text-align:center;padding:10px;background:#0D9488;color:#fff;border-radius:8px;font-size:13px;font-weight:600;text-decoration:none;">Compare Prices Free →</a>
  <div style="text-align:center;margin-top:8px;"><a href="${baseUrl}" target="_blank" rel="noopener" style="font-size:10px;color:#94A3B8;text-decoration:none;">Powered by LinkDiry</a></div>
</div>`,
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(snippets[style]);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Embeddable Price Widget: Add LinkDiry to Your Site"
        description="Embed a free backlink price comparison tool on your blog or website. Let your readers check guest post prices from 50+ marketplaces with this embeddable widget."
        canonical="/embed"
      />
      <TopNav />

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-extrabold text-slate-900 mb-3">Add Price Checks to Your Site</h1>
          <p className="text-lg text-slate-500">Add a "Check Price on LinkDiry" button or widget to your blog. Free backlinks for you, free guest post price comparison tool for your readers.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Config */}
          <div>
            <h2 className="text-lg font-bold text-slate-900 mb-4">Configure</h2>

            <label className="block text-sm font-medium text-slate-700 mb-1">Domain</label>
            <input
              value={domain}
              onChange={e => setDomain(e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm mb-4 focus:outline-none focus:border-brand-500"
              placeholder="e.g. techcrunch.com"
            />

            <label className="block text-sm font-medium text-slate-700 mb-2">Style</label>
            <div className="flex gap-2 mb-6">
              {(['button', 'badge', 'widget'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setStyle(s)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors capitalize ${style === s ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                >
                  {s}
                </button>
              ))}
            </div>

            <label className="block text-sm font-medium text-slate-700 mb-2">Embed Code</label>
            <div className="relative">
              <pre className="bg-slate-900 text-green-400 rounded-xl p-4 text-xs overflow-x-auto max-h-48 whitespace-pre-wrap break-all">{snippets[style]}</pre>
              <button
                onClick={handleCopy}
                className="absolute top-2 right-2 p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
              >
                {copied ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-slate-300" />}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-2">Copy and paste this HTML into your blog post or website.</p>
          </div>

          {/* Preview */}
          <div>
            <h2 className="text-lg font-bold text-slate-900 mb-4">Preview</h2>
            <div className="border border-slate-200 rounded-2xl p-8 bg-slate-50 flex items-center justify-center min-h-[200px]">
              <div dangerouslySetInnerHTML={{ __html: snippets[style] }} />
            </div>
          </div>
        </div>

        <div className="mt-12 bg-slate-50 rounded-2xl p-8">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Why Add This to Your Blog</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">Free for Everyone</h3>
              <p className="text-sm text-slate-500">The widget is completely free. Your readers get price data, you get a contextual backlink.</p>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">Adds Value to Your Content</h3>
              <p className="text-sm text-slate-500">Writing about guest posting or link building? Let readers check real prices inline.</p>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">Always Up to Date</h3>
              <p className="text-sm text-slate-500">Prices update daily from 50+ marketplaces. Your widget always shows current data.</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
